import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    AtSign, Eye, MapPin, TrendingUp, Users, CalendarDays, Award, Wallet, Gavel,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketCard, StatsStatusBadge } from "@/components/MarketCard";
import { SampleBanner } from "@/components/SampleBanner";
import { fetchCreatorProfile, fetchMarketSponsors } from "@/lib/api";
import { compact, longDate, watchTime } from "@/lib/market";

const Metric = ({ icon: Icon, label, value }) => (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className="font-heading text-lg font-semibold tabular mt-0.5">{value}</div>
    </div>
);

/** Creator profile: claimed/verified performance metrics plus their open slots. */
export default function CreatorProfile() {
    const { handle = "" } = useParams();
    const profile = useQuery({
        queryKey: ["creator", handle],
        queryFn: () => fetchCreatorProfile(handle),
        retry: false,
    });
    const sponsors = useQuery({
        queryKey: ["market-sponsors"],
        queryFn: fetchMarketSponsors,
    });

    if (profile.isLoading) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-4">
                <Skeleton className="h-28 w-full rounded-2xl" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
                </div>
                <Skeleton className="h-64 w-full rounded-2xl" />
            </div>
        );
    }

    if (profile.isError) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
                <h1 className="font-heading text-2xl font-bold">Creator not found</h1>
                <p className="text-muted-foreground mt-2 mb-6">
                    No profile for @{handle}. They may not have listed a slot yet.
                </p>
                <Button asChild className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                    <Link to="/market">Browse the market</Link>
                </Button>
            </div>
        );
    }

    const c = profile.data.creator;
    const listings = profile.data.listings || [];
    const missing = profile.data.missing_metrics || [];
    const fits = profile.data.sponsor_fits || [];
    const sponsorNames = new Set(fits.map((f) => f.sponsor_id));
    const sponsorBrands = (sponsors.data?.sponsors || [])
        .filter((s) => sponsorNames.has(s.sponsor_id))
        .map((s) => s.brand);

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <SampleBanner className="mb-6" />

            <Card className="bg-card border-border p-6" data-testid="creator-header">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="grid place-items-center h-16 w-16 rounded-2xl bg-brand/15 text-brand font-heading text-2xl font-bold shrink-0">
                        {c.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{c.name}</h1>
                            <StatsStatusBadge status={c.stats_status} />
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
                            <span className="inline-flex items-center gap-1">
                                <AtSign className="h-3.5 w-3.5" />{c.handle}
                            </span>
                            {c.city ? (
                                <span className="inline-flex items-center gap-1">
                                    <MapPin className="h-3.5 w-3.5" />{c.city}
                                </span>
                            ) : null}
                            <Badge variant="secondary">{c.niche}</Badge>
                            {c.platform_list.map((p) => (
                                <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>
                            ))}
                        </div>
                        {c.bio ? <p className="text-sm text-muted-foreground mt-3 max-w-2xl">{c.bio}</p> : null}
                    </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="creator-metrics">
                    <Metric icon={Users} label="Followers" value={compact(c.followers)} />
                    <Metric icon={Eye} label="Avg views · 30d" value={compact(c.avg_views_30d)} />
                    <Metric icon={TrendingUp} label="Avg watch time" value={watchTime(c.avg_watch_time_sec)} />
                    <Metric icon={CalendarDays} label="Next event" value={c.next_event || "—"} />
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-muted-foreground">
                    {c.next_event_date ? <span>Event date: {longDate(c.next_event_date)}</span> : null}
                    {c.engagement_rate ? <span>Engagement: {(c.engagement_rate * 100).toFixed(1)}%</span> : null}
                    {c.sport_result ? (
                        <span className="inline-flex items-center gap-1">
                            <Award className="h-3.5 w-3.5" />{c.sport_result}
                        </span>
                    ) : null}
                </div>

                {missing.length ? (
                    <div className="mt-4 rounded-lg border border-heat/40 bg-heat/10 px-3 py-2 text-xs text-heat">
                        Metrics missing — new slots stay blocked until these are filled: {missing.join(", ")}.
                    </div>
                ) : null}
            </Card>

            <div className="grid lg:grid-cols-12 gap-6 mt-6">
                <div className="lg:col-span-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2">
                            <Gavel className="h-5 w-5 text-brand" /> Open listings
                        </h2>
                        <span className="text-sm text-muted-foreground tabular">
                            {profile.data.live_count} live
                        </span>
                    </div>
                    {listings.length === 0 ? (
                        <Card className="border-dashed p-10 text-center">
                            <h3 className="font-heading text-lg font-semibold">No slots listed yet</h3>
                            <p className="text-muted-foreground text-sm mt-1">
                                When @{c.handle} lists inventory, it appears here with live bids.
                            </p>
                        </Card>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-4" data-testid="creator-listings">
                            {listings.map((listing) => (
                                <MarketCard key={listing.id} listing={listing} />
                            ))}
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-4 space-y-6">
                    <Card className="bg-card border-border p-4" data-testid="creator-fits">
                        <div className="flex items-center gap-2 mb-2">
                            <Wallet className="h-4 w-4 text-brand" />
                            <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                                Why sponsors fit
                            </h3>
                        </div>
                        {fits.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No registered sponsor targets this niche yet.
                            </p>
                        ) : (
                            <>
                                <p className="text-xs text-muted-foreground mb-2">
                                    {sponsorBrands.join(", ")} target the {c.niche} niche at this audience size.
                                </p>
                                <div className="space-y-2">
                                    {fits.map((f) => (
                                        <div key={f.sponsor_id} className="flex items-center justify-between gap-2 text-sm">
                                            <span className="font-medium truncate">{f.brand}</span>
                                            <span className="text-xs text-muted-foreground tabular shrink-0">
                                                budget {compact(f.budget_usd)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </Card>

                    <Card className="bg-card border-border p-4">
                        <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-2">
                            Stats policy
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            Metrics marked <strong>claimed</strong> are creator-reported and unverified.
                            Verification via connected account or screenshot comes before payouts scale.
                        </p>
                    </Card>
                </aside>
            </div>
        </div>
    );
}


